import React, { useState, useEffect, useRef, KeyboardEvent } from "react"
import { Tooltip, Typography, InputBase, InputAdornment, IconButton } from "@material-ui/core"
import { Check as CheckIcon, Edit as EditIcon } from "@material-ui/icons"
import clsx from "clsx"

import useStyles from "@/pages/Admin/Flow/FlowConstructor/FlowNameInlineEdit/styles"
import { usePermissionStateStore } from "@/store/PermissionState"
import { PERMISSION_DENIED_MESSAGE } from "@/components/PermissionGate/BasePermissionGate"

type FlowNameInlineEditProps = {
	name: string
	onSave: (name: string) => Promise<void>
}

const FlowNameInlineEdit: React.FC<FlowNameInlineEditProps> = ({ name, onSave }) => {
	const FLOW_NAME_MAX_LENGTH = 100
	const classes = useStyles()
	const { checkPermission } = usePermissionStateStore()
	const [isEditing, setIsEditing] = useState(false)
	const [displayName, setDisplayName] = useState(name)
	const [inputValue, setInputValue] = useState("")
	const [isSaving, setIsSaving] = useState(false)
	const isSavingRef = useRef(false)

	useEffect(() => {
		setDisplayName(name)
	}, [name])

	const startEditing = () => {
		if (isSaving) return
		setInputValue(displayName)
		setIsEditing(true)
	}

	const cancelEditing = () => {
		setIsEditing(false)
	}

	const commitEdit = async () => {
		if (isSavingRef.current) return
		const trimmed = inputValue.trim()
		setIsEditing(false)
		if (!trimmed || trimmed === displayName) return

		isSavingRef.current = true
		setIsSaving(true)
		const previous = displayName
		setDisplayName(trimmed)
		try {
			await onSave(trimmed)
		} catch {
			setDisplayName(previous)
		} finally {
			isSavingRef.current = false
			setIsSaving(false)
		}
	}

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		const eventKeyToAction: Record<string, () => void> = {
			Enter: commitEdit,
			Escape: cancelEditing
		}

		if (event.key in eventKeyToAction) {
			event.preventDefault()
			eventKeyToAction[event.key]()
		}
	}

	if (isEditing) {
		return (
			<InputBase
				className={classes.input}
				value={inputValue}
				onChange={(event) => setInputValue(event.target.value)}
				onBlur={commitEdit}
				onKeyDown={handleKeyDown}
				autoFocus
				inputProps={{ maxLength: FLOW_NAME_MAX_LENGTH }}
				endAdornment={(
					<InputAdornment position="end">
						<IconButton
							size="small"
							// Prevents the input blur from firing before the click, which would trigger commitEdit twice
							onMouseDown={(event) => event.preventDefault()}
							onClick={commitEdit}
						>
							<CheckIcon fontSize="small" className={classes.confirmIcon} />
						</IconButton>
					</InputAdornment>
				)}
			/>
		)
	}

	const canUpdateBot = checkPermission("chat-bot", "update")

	return (
		<Tooltip
			title={canUpdateBot ? displayName : PERMISSION_DENIED_MESSAGE}
		>
			<div
				className={clsx(classes.textWrapper, { [classes.saving]: isSaving })}
				onClick={canUpdateBot ? startEditing : undefined}
			>
				<Typography className={classes.text}>
					<strong>Bot:</strong> {displayName}
				</Typography>

				<EditIcon className={classes.editIcon} fontSize="small" />
			</div>
		</Tooltip>
	)
}

export default FlowNameInlineEdit
